import React, { useMemo } from 'react';
import { Card, Empty, Flex, Segmented, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  DELIVERY_OUTCOME_GROUPS,
  DELIVERY_OUTCOMES,
  DeliveryMatrixGroupBy,
  DeliveryMatrixRow,
  DeliveryOutcomeCounts,
  DeliveryWindow,
  daysInWindow,
  groupCounts,
  sumCounts,
} from '../../../domain/entities/DeliveryDashboard';
import { GROUP_COLORS } from './deliveryOutcomeStyles';

interface DeliveryMatrixProps {
  rows: DeliveryMatrixRow[];
  window: DeliveryWindow;
  groupBy: DeliveryMatrixGroupBy;
  onGroupByChange: (groupBy: DeliveryMatrixGroupBy) => void;
  onCellClick: (key: string, day: string) => void;
  onRowClick: (key: string) => void;
}

const todayKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const shortLabel = (label: string) => label.replace(/^https:\/\/t\.me\//, '');

const CellBar: React.FC<{ counts: DeliveryOutcomeCounts }> = ({ counts }) => {
  const groups = groupCounts(counts);
  const total = DELIVERY_OUTCOME_GROUPS.reduce((sum, group) => sum + groups[group], 0);
  if (total === 0) return <div className="delivery-matrix-bar delivery-matrix-bar-empty" />;

  return (
    <div className="delivery-matrix-bar">
      {DELIVERY_OUTCOME_GROUPS.filter((group) => groups[group] > 0).map((group) => (
        <span key={group} style={{ flexGrow: groups[group], background: GROUP_COLORS[group] }} />
      ))}
    </div>
  );
};

const DeliveryMatrix: React.FC<DeliveryMatrixProps> = ({ rows, window, groupBy, onGroupByChange, onCellClick, onRowClick }) => {
  const { t, i18n } = useTranslation();
  const days = useMemo(() => daysInWindow(window), [window]);
  const today = todayKey();
  const dayFormat = useMemo(() => new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'short' }), [i18n.language]);

  const tooltip = (label: string, day: string, counts: DeliveryOutcomeCounts) => (
    <div>
      <div><strong>{shortLabel(label)}</strong> · {dayFormat.format(new Date(`${day}T00:00:00`))}</div>
      {DELIVERY_OUTCOMES.filter((outcome) => (counts[outcome] ?? 0) > 0).map((outcome) => (
        <div key={outcome}>{t(`delivery-outcome-${outcome}`, { ns: 'labels' })}: {counts[outcome]}</div>
      ))}
      {sumCounts(counts) === 0 && <div>{t('delivery-matrix-empty-cell', { ns: 'labels' })}</div>}
    </div>
  );

  return (
    <Card
      size="small"
      title={t('delivery-matrix-title', { ns: 'titles' })}
      extra={(
        <Segmented<DeliveryMatrixGroupBy>
          size="small"
          value={groupBy}
          onChange={onGroupByChange}
          options={[
            { value: 'account', label: t('delivery-matrix-by-account', { ns: 'labels' }) },
            { value: 'channel', label: t('delivery-matrix-by-channel', { ns: 'labels' }) },
          ]}
        />
      )}
    >
      <Flex gap={12} wrap="wrap" className="delivery-matrix-legend">
        {DELIVERY_OUTCOME_GROUPS.map((group) => (
          <Flex key={group} gap={6} align="center">
            <span className="delivery-matrix-swatch" style={{ background: GROUP_COLORS[group] }} />
            <Typography.Text type="secondary">{t(`delivery-group-${group}`, { ns: 'labels' })}</Typography.Text>
          </Flex>
        ))}
      </Flex>

      {rows.length === 0 ? (
        <Empty description={t('delivery-matrix-empty', { ns: 'labels' })} />
      ) : (
        <div className="delivery-matrix-scroll">
          <table className="delivery-matrix">
            <thead>
              <tr>
                <th className="delivery-matrix-label" />
                {days.map((day) => (
                  <th key={day} className={day === today ? 'delivery-matrix-today' : undefined}>
                    {dayFormat.format(new Date(`${day}T00:00:00`))}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const cellsByDay = new Map(row.cells.map((cell) => [cell.day, cell.counts]));
                return (
                  <tr key={row.key}>
                    <th className="delivery-matrix-label">
                      <button type="button" className="delivery-matrix-row-button" onClick={() => onRowClick(row.key)}>
                        <span className="delivery-matrix-row-label">{shortLabel(row.label)}</span>
                        {row.problems > 0 && (
                          <Typography.Text type="danger" className="delivery-matrix-row-problems">
                            {t('delivery-matrix-problems', { ns: 'labels', count: row.problems, total: row.total })}
                          </Typography.Text>
                        )}
                      </button>
                    </th>
                    {days.map((day) => {
                      const counts = cellsByDay.get(day) ?? {};
                      return (
                        <td key={day} className={day === today ? 'delivery-matrix-today' : undefined}>
                          <Tooltip title={tooltip(row.label, day, counts)}>
                            <button
                              type="button"
                              className="delivery-matrix-cell"
                              disabled={sumCounts(counts) === 0}
                              onClick={() => onCellClick(row.key, day)}
                            >
                              <CellBar counts={counts} />
                            </button>
                          </Tooltip>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
};

export default DeliveryMatrix;
